import React, { useCallback } from 'react';
import { formatJSON } from '../../../utils/apiRenderingUtils';

const styles = {
  container: {
    position: 'relative',
    maxWidth: '100%',
    margin: '16px 0',
  },
  codeBlock: {
    backgroundColor: '#1e1e1e',
    borderRadius: '6px',
    padding: '16px',
    position: 'relative',
    fontFamily: 'monospace',
    fontSize: '14px',
    overflow: 'auto',
  },
  copyButton: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    zIndex: 1,
  },
  // Syntax highlighting colors
  keyword: { color: '#c678dd' }, // Purple
  string: { color: '#98c379' }, // Green
  number: { color: '#61afef' }, // Blue
  boolean: { color: '#e06c75' }, // Red
  null: { color: '#c678dd' }, // Purple
  property: { color: '#e5c07b' }, // Yellow
  punctuation: { color: '#abb2bf' }, // Light Gray
  method: { color: '#61afef' }, // Blue
  url: { color: '#98c379' }, // Green
  header: { color: '#e5c07b' }, // Yellow
  lineIndent: {
    paddingLeft: '20px',
  },
  data: { color: '#9CDCFE' },
};

const highlightJson = (obj, styles) => {
  if (!obj && obj !== 0 && obj !== false) return <span style={{ color: '#fff' }}>No data provided</span>;

  const renderValue = (value) => {
    if (value === null) return <span style={styles.null}>null</span>;
    if (typeof value === 'string') return <span style={styles.string}>"{value}"</span>;
    if (typeof value === 'number') return <span style={styles.number}>{value}</span>;
    if (typeof value === 'boolean') return <span style={styles.boolean}>{value.toString()}</span>;
    return value;
  };

  const renderJson = (data, level = 0) => {
    const indent = level * 20;

    if (Array.isArray(data)) {
      return (
        <>
          <span style={styles.punctuation}>[</span>
          <div style={{ marginLeft: 20 }}>
            {data.map((item, index) => (
              <div key={index}>
                {typeof item === 'object' && item !== null ? renderJson(item, level + 1) : renderValue(item)}
                {index < data.length - 1 && <span style={styles.punctuation}>,</span>}
              </div>
            ))}
          </div>
          <div>
            <span style={styles.punctuation}>]</span>
          </div>
        </>
      );
    }

    if (typeof data === 'object' && data !== null) {
      return (
        <>
          <span style={styles.punctuation}>{`{`}</span>
          <div style={{ marginLeft: 20 }}>
            {Object.entries(data).map(([key, value], index, array) => (
              <div key={key}>
                <span style={styles.property}>"{key}"</span>
                <span style={styles.punctuation}>: </span>
                {typeof value === 'object' && value !== null ? renderJson(value, level + 1) : renderValue(value)}
                {index < array.length - 1 && <span style={styles.punctuation}>,</span>}
              </div>
            ))}
          </div>
          <div>
            <span style={styles.punctuation}>{`}`}</span>
          </div>
        </>
      );
    }

    return renderValue(data);
  };

  try {
    const response = renderJson(typeof obj === 'string' ? JSON.parse(obj) : obj);
    return response;
  } catch (e) {
    return <span style={{ color: '#fff' }}>{obj}</span>;
  }
};

const highlightCode = (code, styles) => {
  if (!code) return <span style={{ color: '#fff' }}>No code provided</span>;

  const lines = code.split('\n');

  return lines.map((line, i) => {
    // Handle method in fetch
    if (line.includes('"method"')) {
      return (
        <div key={i} style={styles.lineIndent}>
          <span style={styles.property}>"method"</span>
          <span style={styles.punctuation}>: </span>
          <span style={styles.method}>"post"</span>
          <span style={styles.punctuation}>,</span>
        </div>
      );
    }

    // Handle headers section
    if (line.trim() === '"headers": {') {
      return (
        <div key={i} style={styles.lineIndent}>
          <span style={styles.property}>"headers"</span>
          <span style={styles.punctuation}>: {'{}'}</span>
        </div>
      );
    }

    // Handle header entries
    if (line.includes('Content-Type')) {
      return (
        <div key={i} style={{ paddingLeft: '40px' }}>
          <span style={styles.header}>"Content-Type"</span>
          <span style={styles.punctuation}>: </span>
          <span style={styles.string}>"application/json"</span>
        </div>
      );
    }

    // Handle body in fetch
    if (line.includes('"body"')) {
      const bodyMatch = line.match(/"body":\s*"(.+)"/);
      if (bodyMatch) {
        const bodyContent = formatJSON(bodyMatch[1].replace(/\\"/g, '"'));
        return (
          <div key={i} style={styles.lineIndent}>
            <span style={styles.property}>"body"</span>
            <span style={styles.punctuation}>: </span>
            <span style={styles.string}>"{bodyContent}"</span>
          </div>
        );
      }
    }

    // Handle curl commands
    if (line.includes('curl')) {
      const parts = line.split('curl ');
      return (
        <div key={i}>
          <span style={styles.keyword}>curl</span> <span style={styles.url}>{parts[1]}</span>
        </div>
      );
    }

    // Handle --data flag in curl
    if (line.trim().startsWith('--data')) {
      const dataMatch = line.match(/'(.+)'/);
      if (dataMatch) {
        try {
          const jsonData = JSON.parse(dataMatch[1]);
          return (
            <div key={i} style={styles.lineIndent}>
              <span style={styles.property}>--data</span>
              <span style={styles.punctuation}> '</span>
              <span style={styles.data}>{JSON.stringify(jsonData, null, 2)}</span>
              <span style={styles.punctuation}>'</span>
            </div>
          );
        } catch (e) {
          return (
            <div key={i} style={styles.lineIndent}>
              <span style={styles.property}>--data</span>
              <span style={styles.string}> '{dataMatch[1]}'</span>
            </div>
          );
        }
      }
    }

    // Handle headers in curl
    if (line.includes('--header')) {
      const headerMatch = line.match(/'(.+)'/);
      if (headerMatch) {
        const [key, value] = headerMatch[1].split(': ');
        return (
          <div key={i} style={styles.lineIndent}>
            <span style={styles.property}>--header</span>
            <span style={styles.punctuation}> '</span>
            <span style={styles.header}>{key}</span>
            <span style={styles.punctuation}>: </span>
            <span style={styles.string}>{value}</span>
            <span style={styles.punctuation}>'</span>
          </div>
        );
      }
    }

    // Handle URL flag in curl
    if (line.includes('--url')) {
      const urlMatch = line.match(/'(.+)'/);
      if (urlMatch) {
        return (
          <div key={i} style={styles.lineIndent}>
            <span style={styles.property}>--url</span>
            <span style={styles.punctuation}> '</span>
            <span style={styles.url}>{urlMatch[1]}</span>
            <span style={styles.punctuation}>'</span>
          </div>
        );
      }
    }

    // Handle closing braces and other symbols
    if (line.includes('}')) {
      return (
        <div key={i} style={{ paddingLeft: line.indexOf('}') > 2 ? '20px' : '0' }}>
          <span style={styles.punctuation}>{'}'}</span>
          {line.includes(');') && <span style={styles.punctuation}>);</span>}
        </div>
      );
    }

    // Default case for other lines
    return <div key={i}>{line}</div>;
  });
};

const highlightFetch = (code, styles) => {
  if (!code) return <span style={{ color: '#fff' }}>No code provided</span>;

  const lines = code.split('\n');
  const result = [];

  let inHeaders = false;
  let headerIndent = false;

  lines.forEach((line, i) => {
    // Handle method
    if (line.includes('"method"')) {
      const methodMatch = line.match(/"method":\s*"([^"]+)"/);
      result.push(
        <div key={`line-${i}`} style={styles.lineIndent}>
          <span style={styles.property}>"method"</span>
          <span style={styles.punctuation}>: </span>
          <span style={styles.method}>"{methodMatch?.[1] || 'get'}"</span>
          <span style={styles.punctuation}>,</span>
        </div>
      );
      return;
    }

    // Handle headers start
    if (line.includes('"headers"')) {
      inHeaders = true;
      result.push(
        <div key={`line-${i}`} style={styles.lineIndent}>
          <span style={styles.property}>"headers"</span>
          <span style={styles.punctuation}>: {'{'}</span>
        </div>
      );
      headerIndent = true;
      return;
    }

    // Handle header entries
    if (inHeaders && line.includes(':') && !line.includes('}')) {
      const [key, value] = line.split(':').map((part) => part.trim().replace(/[",]/g, ''));
      result.push(
        <div key={`line-${i}`} style={{ paddingLeft: '40px' }}>
          <span style={styles.header}>"{key}"</span>
          <span style={styles.punctuation}>: </span>
          <span style={styles.string}>"{value}"</span>
          <span style={styles.punctuation}>,</span>
        </div>
      );
      return;
    }

    // Handle headers end
    if (inHeaders && line.includes('}')) {
      inHeaders = false;
      headerIndent = false;
      result.push(
        <div key={`line-${i}`} style={styles.lineIndent}>
          <span style={styles.punctuation}>{'}'}</span>
          {line.includes('})') && (
            <>
              <span style={styles.punctuation}>{'}'}</span>
              <span style={styles.punctuation}>)</span>
            </>
          )}
        </div>
      );
      return;
    }

    // Default case
    result.push(
      <div key={`line-${i}`} style={headerIndent ? { paddingLeft: '40px', color: '#ffff' } : { color: '#ffff' }}>
        {line}
      </div>
    );
  });

  return result;
};

const SyntaxHighlighter = (props) => {
  const { code, language = 'json' } = props;

  const renderHighlighted = useCallback((language, code) => {
    switch (language) {
      case 'json':
        return highlightJson(code, styles);
      case 'fetch':
        return highlightFetch(code, styles);
      default:
        return highlightCode(code, styles);
    }
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.codeBlock}>{renderHighlighted(language, code)}</div>
    </div>
  );
};

export default SyntaxHighlighter;
